import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  HiOutlineMagnifyingGlass,
  HiOutlineArrowUpTray,
  HiOutlineArrowDownTray,
  HiOutlineTrash,
  HiOutlineXMark,
  HiOutlineDocumentText,
  HiOutlineFunnel,
  HiOutlineExclamationTriangle,
} from 'react-icons/hi2';
import {
  BsGrid3X3GapFill,
  BsListUl,
  BsFiletypePdf,
  BsFiletypePptx,
  BsFiletypeXlsx,
  BsFiletypeDocx,
  BsFiletypePng,
  BsFiletypeJpg,
  BsFileEarmark,
} from 'react-icons/bs';
import Spinner from '../Spinner/Spinner';
import CustomDropdown from '../CustomDropdown/CustomDropdown';
import { fetchBrandFiles, uploadBrandFile, deleteBrandFile, getDownloadUrl, BrandFile, BrandFileFilters } from '../../services/brandFilesService';
import BrandFilesFilter from '../BrandFilesFilter/BrandFilesFilter';
import { fetchBrandsByUltimateParent } from '../../services/brandService';
import useAuthStore from '../../stores/authStore';
import './BrandFilesTab.css';

interface BrandFilesTabProps {
  brandName?: string;
  isUltimateParent?: boolean;
}

const BLOCKED_EXTENSIONS = new Set([
  '.exe', '.bat', '.cmd', '.sh', '.msi', '.dll', '.com', '.scr', '.ps1',
  '.vbs', '.wsf', '.cpl', '.jar', '.inf', '.reg', '.rgs', '.sct', '.pif',
]);

const ACCEPTED_FILE_TYPES = [
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
  '.csv', '.txt', '.rtf',
  '.png', '.jpg', '.jpeg', '.gif', '.svg', '.webp', '.bmp',
  '.zip', '.rar', '.7z',
  '.mp4', '.mov', '.avi', '.mp3', '.wav',
].join(',');

function isBlockedFile(fileName: string): boolean {
  const ext = '.' + (fileName.split('.').pop() || '').toLowerCase();
  return BLOCKED_EXTENSIONS.has(ext);
}

type ViewMode = 'grid' | 'list';

function getFileIcon(type: string) {
  if (type.includes('pdf')) return <BsFiletypePdf className="bf-file-icon bf-icon-pdf" />;
  if (type.includes('presentation') || type.includes('powerpoint')) return <BsFiletypePptx className="bf-file-icon bf-icon-pptx" />;
  if (type.includes('spreadsheet') || type.includes('excel')) return <BsFiletypeXlsx className="bf-file-icon bf-icon-xlsx" />;
  if (type.includes('word') || type.includes('document')) return <BsFiletypeDocx className="bf-file-icon bf-icon-docx" />;
  if (type.includes('png')) return <BsFiletypePng className="bf-file-icon bf-icon-img" />;
  if (type.includes('jpg') || type.includes('jpeg')) return <BsFiletypeJpg className="bf-file-icon bf-icon-img" />;
  if (type.includes('image')) return <BsFiletypePng className="bf-file-icon bf-icon-img" />;
  return <BsFileEarmark className="bf-file-icon bf-icon-default" />;
}

function getFileExtension(name: string): string {
  const parts = name.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : 'FILE';
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function BrandFilesTab({ brandName, isUltimateParent = false }: BrandFilesTabProps) {
  const user = useAuthStore((s) => s.user);
  const [files, setFiles] = useState<BrandFile[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [filtering, setFiltering] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [cancelledMessage, setCancelledMessage] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadXhrRef = useRef<XMLHttpRequest | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<BrandFileFilters>({});
  const filtersRef = useRef<BrandFileFilters>({});
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Pagination
  const PAGE_SIZE = 20;
  const [totalCount, setTotalCount] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const hasMore = files.length < totalCount;

  // For ultimate parent: child brands to select from
  const [childBrands, setChildBrands] = useState<string[]>([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [loadingBrands, setLoadingBrands] = useState(false);

  const decodedBrandName = brandName ? decodeURIComponent(brandName) : '';

  const loadFiles = useCallback((filters: BrandFileFilters, isInitial = false) => {
    if (!decodedBrandName) return;
    if (isInitial) setInitialLoading(true);
    else setFiltering(true);
    fetchBrandFiles(decodedBrandName, isUltimateParent, PAGE_SIZE, 0, filters)
      .then((result) => {
        setFiles(result.files);
        setTotalCount(result.totalCount);
      })
      .finally(() => {
        setInitialLoading(false);
        setFiltering(false);
      });
  }, [decodedBrandName, isUltimateParent]);

  // Fetch first page of files
  useEffect(() => {
    loadFiles({}, true);
  }, [loadFiles]);

  const handleLoadMore = async () => {
    if (loadingMore || !decodedBrandName) return;
    setLoadingMore(true);
    try {
      const result = await fetchBrandFiles(decodedBrandName, isUltimateParent, PAGE_SIZE, files.length, filtersRef.current);
      setFiles((prev) => [...prev, ...result.files]);
      setTotalCount(result.totalCount);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleApplyFilters = (filters: BrandFileFilters) => {
    setAppliedFilters(filters);
    filtersRef.current = filters;
    setFilterOpen(false);
    loadFiles(filters);
  };

  const activeFilterCount = Object.values(appliedFilters).filter(Boolean).length;

  // Fetch child brands when upload modal opens for ultimate parent
  useEffect(() => {
    if (!uploadModalOpen || !isUltimateParent || !decodedBrandName) return;
    setLoadingBrands(true);
    fetchBrandsByUltimateParent(decodedBrandName)
      .then((brands) => {
        const names = brands.map((b: any) => b.name || b.brand_name).filter(Boolean);
        // Include the ultimate parent itself as an option
        const allOptions = [decodedBrandName, ...names.filter((n: string) => n !== decodedBrandName)];
        setChildBrands(allOptions);
        if (allOptions.length > 0) setSelectedBrand(allOptions[0]);
      })
      .finally(() => setLoadingBrands(false));
  }, [uploadModalOpen, isUltimateParent, decodedBrandName]);

  const filteredFiles = useMemo(() => {
    return files.filter((f) => {
      const matchesSearch =
        searchQuery === '' ||
        f.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        f.uploadedBy.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (f.description || '').toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    });
  }, [files, searchQuery]);

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const validateFile = (file: File): string | null => {
    if (isBlockedFile(file.name)) {
      const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
      return `File type "${ext}" is not allowed. Please upload documents, images, or other supported files.`;
    }
    if (file.size > 50 * 1024 * 1024) {
      return 'File size exceeds 50MB limit.';
    }
    return null;
  };

  const handleFileSelect = (file: File | null) => {
    setUploadError('');
    if (file) {
      const error = validateFile(file);
      if (error) {
        setUploadError(error);
        return;
      }
    }
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || !decodedBrandName) return;

    const targetBrand = isUltimateParent ? selectedBrand : decodedBrandName;
    if (!targetBrand) return;

    const uploaderName = user?.name || user?.email || 'Unknown';
    const ultimateParent = isUltimateParent ? decodedBrandName : undefined;

    setUploading(true);
    setUploadProgress(0);
    setUploadError('');
    try {
      const newFile = await uploadBrandFile(
        targetBrand,
        selectedFile,
        uploadDescription,
        uploaderName,
        ultimateParent,
        (percent) => setUploadProgress(percent),
        (xhr) => { uploadXhrRef.current = xhr; },
      );
      setFiles((prev) => [newFile, ...prev]);
      setTotalCount((prev) => prev + 1);
      setUploadModalOpen(false);
      setSelectedFile(null);
      setUploadDescription('');
      setUploadProgress(0);
    } catch (err: any) {
      console.error('Upload failed:', err);
      setUploadError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleCloseModal = () => {
    setUploadModalOpen(false);
    setSelectedFile(null);
    setUploadDescription('');
    setUploadError('');
    setUploadProgress(0);
  };

  const handleCancelUpload = () => {
    const wasUploading = uploading;
    if (uploadXhrRef.current) {
      uploadXhrRef.current.abort();
      uploadXhrRef.current = null;
    }
    setUploading(false);
    setUploadProgress(0);
    setUploadError('');
    if (wasUploading) {
      setCancelledMessage('Upload cancelled.');
      setTimeout(() => {
        setCancelledMessage('');
        setUploadModalOpen(false);
        setSelectedFile(null);
        setUploadDescription('');
      }, 2000);
    } else {
      setUploadModalOpen(false);
      setSelectedFile(null);
      setUploadDescription('');
    }
  };

  const handleDeleteClick = (fileId: string, fileName: string) => {
    setDeleteConfirm({ id: fileId, name: fileName });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      const success = await deleteBrandFile(deleteConfirm.id);
      if (success) {
        setFiles((prev) => prev.filter((f) => f.id !== deleteConfirm.id));
        setTotalCount((prev) => prev - 1);
      }
    } finally {
      setDeleting(false);
      setDeleteConfirm(null);
    }
  };

  const handleDownload = async (fileId: string, fileName: string) => {
    const url = await getDownloadUrl(fileId);
    if (url) {
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      const error = isBlockedFile(droppedFile.name)
        ? `File type ".${(droppedFile.name.split('.').pop() || '').toLowerCase()}" is not allowed.`
        : null;
      if (error) {
        setUploadError(error);
      } else {
        setUploadError('');
        setSelectedFile(droppedFile);
      }
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  if (initialLoading) {
    return (
      <div className="brand-files-tab" style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <Spinner size="small" />
      </div>
    );
  }

  return (
    <div className="brand-files-tab">
      {/* Header: Search + Upload + View Toggle */}
      <div className="bf-header">
        <div className="bf-search-bar">
          <HiOutlineMagnifyingGlass className="bf-search-icon" />
          <input
            type="text"
            placeholder="Search files..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bf-search-input"
          />
        </div>
        <div className="bf-header-right">
          <button className="bf-filter-btn" onClick={() => setFilterOpen(true)}>
            <HiOutlineFunnel />
            Filters
            {activeFilterCount > 0 && <span className="bf-filter-badge">{activeFilterCount}</span>}
          </button>
          <div className="bf-view-toggle">
            <button
              className={`bf-view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid view"
            >
              <BsGrid3X3GapFill />
            </button>
            <button
              className={`bf-view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="List view"
            >
              <BsListUl />
            </button>
          </div>
          <button className="bf-upload-btn" onClick={() => setUploadModalOpen(true)}>
            <HiOutlineArrowUpTray />
            Upload
          </button>
        </div>
      </div>

      {/* File Count */}
      {filteredFiles.length > 0 && (
        <p className="bf-file-count">
          {filteredFiles.length} of {totalCount} file{totalCount !== 1 ? 's' : ''}
        </p>
      )}

      {/* Content */}
      {filtering ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spinner size="small" />
        </div>
      ) : filteredFiles.length > 0 ? (
        viewMode === 'grid' ? (
          /* ===== GRID VIEW ===== */
          <div className="bf-grid">
            {filteredFiles.map((file) => (
              <div key={file.id} className="bf-card">
                <div className="bf-card-icon-area">
                  {getFileIcon(file.type)}
                  <span className="bf-card-ext">{getFileExtension(file.name)}</span>
                </div>
                <div className="bf-card-body">
                  <p className="bf-card-name" title={file.name}>{file.name}</p>
                  {file.description && (
                    <p className="bf-card-description" title={file.description}>{file.description}</p>
                  )}
                  {isUltimateParent && (
                    <span className="bf-card-brand-tag">{file.brandName}</span>
                  )}
                  <div className="bf-card-meta">
                    <span className="bf-card-size">{formatFileSize(file.size)}</span>
                    <span className="bf-card-dot"></span>
                    <span className="bf-card-date">{formatDate(file.uploadedAt)}</span>
                  </div>
                  <div className="bf-card-footer">
                    <span className="bf-card-uploader">{file.uploadedBy}</span>
                    <div className="bf-card-actions">
                      <button className="bf-icon-btn" title="Download" onClick={() => handleDownload(file.id, file.name)}><HiOutlineArrowDownTray /></button>
                      <button className="bf-icon-btn bf-delete-btn" title="Delete" onClick={() => handleDeleteClick(file.id, file.name)}><HiOutlineTrash /></button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* ===== LIST VIEW ===== */
          <div className="bf-list-wrapper">
            <table className="bf-list-table">
              <thead>
                <tr>
                  <th>Name</th>
                  {isUltimateParent && <th>Brand</th>}
                  <th>Description</th>
                  <th>Size</th>
                  <th>Uploaded By</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredFiles.map((file) => (
                  <tr key={file.id}>
                    <td>
                      <div className="bf-list-name">
                        {getFileIcon(file.type)}
                        <span>{file.name}</span>
                      </div>
                    </td>
                    {isUltimateParent && <td>{file.brandName}</td>}
                    <td>
                      <span className="bf-list-description">{file.description || '—'}</span>
                    </td>
                    <td>{formatFileSize(file.size)}</td>
                    <td>{file.uploadedBy}</td>
                    <td>{formatDate(file.uploadedAt)}</td>
                    <td>
                      <div className="bf-list-actions">
                        <button className="bf-icon-btn" title="Download" onClick={() => handleDownload(file.id, file.name)}><HiOutlineArrowDownTray /></button>
                        <button className="bf-icon-btn bf-delete-btn" title="Delete" onClick={() => handleDeleteClick(file.id, file.name)}><HiOutlineTrash /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        <div className="bf-empty-state">
          <HiOutlineDocumentText className="bf-empty-icon" />
          <h3 className="bf-empty-title">No Brand Files</h3>
          <p className="bf-empty-desc">
            {searchQuery || activeFilterCount > 0
              ? 'No files match your search or filter criteria.'
              : 'No files have been uploaded for this brand yet.'}
          </p>
          <button className="bf-upload-btn" onClick={() => setUploadModalOpen(true)}>
            <HiOutlineArrowUpTray /> Upload File
          </button>
        </div>
      )}

      {/* Load More */}
      {hasMore && (
        <div className="bf-load-more">
          <button
            className="bf-load-more-btn"
            onClick={handleLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading...' : `Load More (${files.length} of ${totalCount})`}
          </button>
        </div>
      )}

      {/* Upload Modal */}
      {uploadModalOpen && (
        <div className="bf-overlay" onClick={uploading ? handleCancelUpload : handleCloseModal}>
          <div className="bf-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bf-modal-header">
              <h3>Upload Brand File</h3>
              <button className="bf-modal-close" onClick={uploading ? handleCancelUpload : handleCloseModal}>
                <HiOutlineXMark />
              </button>
            </div>
            <div className="bf-modal-body">
              {/* Cancelled message shown inside modal */}
              {cancelledMessage && (
                <div className="bf-upload-error" style={{ marginBottom: '1rem' }}>
                  <HiOutlineExclamationTriangle />
                  <span>{cancelledMessage}</span>
                </div>
              )}
              {!selectedFile ? (
                <div
                  className={`bf-dropzone ${dragOver ? 'drag-over' : ''}`}
                  onClick={() => fileInputRef.current?.click()}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  <HiOutlineArrowUpTray className="bf-dropzone-icon" />
                  <p className="bf-dropzone-text">
                    Drag & drop or <span>browse</span>
                  </p>
                  <p className="bf-dropzone-hint">PDF, Word, Excel, PowerPoint, Images, and more (max 50MB)</p>
                </div>
              ) : (
                <div className="bf-selected-file">
                  {getFileIcon(selectedFile.type)}
                  <span className="bf-selected-name">{selectedFile.name}</span>
                  <span className="bf-selected-size">{formatFileSize(selectedFile.size)}</span>
                  {!uploading && (
                    <button className="bf-remove-file" onClick={() => { setSelectedFile(null); setUploadProgress(0); }}>
                      <HiOutlineXMark />
                    </button>
                  )}
                </div>
              )}

              {/* Upload Progress Bar */}
              {uploading && (
                <div className="bf-progress-wrapper">
                  <div className="bf-progress-bar">
                    <div className="bf-progress-fill" style={{ width: `${uploadProgress}%` }} />
                  </div>
                  <span className="bf-progress-text">{uploadProgress}%</span>
                </div>
              )}

              {/* Error message */}
              {uploadError && (
                <div className="bf-upload-error">
                  <HiOutlineExclamationTriangle />
                  <span>{uploadError}</span>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_FILE_TYPES}
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileSelect(file);
                  e.target.value = '';
                }}
              />

              {/* Brand selector for ultimate parents */}
              {isUltimateParent && (
                <div className="bf-brand-select-wrapper">
                  <label className="bf-select-label">Upload to brand</label>
                  {loadingBrands ? (
                    <div style={{ padding: '8px 0' }}><Spinner size="small" /></div>
                  ) : (
                    <CustomDropdown
                      value={selectedBrand}
                      options={childBrands}
                      onChange={(val) => setSelectedBrand(val)}
                      placeholder="Select brand..."
                    />
                  )}
                </div>
              )}

              <div className="bf-brand-select-wrapper">
                <label className="bf-select-label">Description</label>
                <input
                  type="text"
                  className="bf-description-input"
                  placeholder="Tell AI what is in this file and when to use it..."
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  disabled={uploading}
                />
              </div>
            </div>
            <div className="bf-modal-footer">
              <button className="bf-cancel-btn" onClick={handleCancelUpload}>
                {uploading ? 'Cancel Upload' : 'Cancel'}
              </button>
              <button
                className="bf-submit-btn"
                disabled={!selectedFile || uploading || (isUltimateParent && !selectedBrand)}
                onClick={handleUpload}
              >
                {uploading ? `Uploading... ${uploadProgress}%` : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="bf-overlay" onClick={() => { if (!deleting) setDeleteConfirm(null); }}>
          <div className="bf-modal bf-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bf-modal-header">
              <h3>Delete File</h3>
              <button className="bf-modal-close" onClick={() => { if (!deleting) setDeleteConfirm(null); }}>
                <HiOutlineXMark />
              </button>
            </div>
            <div className="bf-modal-body">
              <div className="bf-delete-confirm-content">
                <HiOutlineExclamationTriangle className="bf-delete-warn-icon" />
                <p>Are you sure you want to delete <strong>{deleteConfirm.name}</strong>?</p>
                <p className="bf-delete-hint">This action cannot be undone.</p>
              </div>
            </div>
            <div className="bf-modal-footer">
              <button className="bf-cancel-btn" onClick={() => setDeleteConfirm(null)} disabled={deleting}>
                Cancel
              </button>
              <button className="bf-submit-btn bf-delete-confirm-btn" onClick={handleDeleteConfirm} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter Panel */}
      <BrandFilesFilter
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApplyFilters={handleApplyFilters}
        showBrandFilter={false}
        appliedFilters={appliedFilters}
      />
    </div>
  );
}
